import PromiseWorker from "promise-worker";

const rawWorker = new Worker(
   /* webpackChunkName: "IDBworker" */ new URL(
      "./IDBworker/IDBWorker.js",
      import.meta.url,
   ),
);
const IDBWorker = new PromiseWorker(rawWorker);

function argTreater(args) {
   return args.filter((arg) => arg !== undefined);
}

/**
 * Helper Functions for IndexedDB, which allow for axios-esqe calls to IndexedDB over a worker.
 */
const ECLIndexedDBWrapper = {
   /**
    * see https://developer.mozilla.org/en-US/docs/Web/API/IDBObjectStore/get
    * @param {string} objectStore - Name of object store you want to access
    * @param {key|IDBKeyRange} key - Valid IndexedDB key (Number or string preferred), or IDBKeyRange
    * @returns {object} Promise that resolves into the first result that matches your query.
    */
   get: async (objectStore, key) => {
      if (objectStore === undefined) {
         throw new Error("No object store supplied");
      }
      if (key === undefined) {
         throw new Error("No key supplied");
      }
      const args = argTreater([key]);
      return IDBWorker.postMessage({
         type: "get",
         objectStore: objectStore,
         args: args,
      });
   },
   /**
    * see https://developer.mozilla.org/en-US/docs/Web/API/IDBObjectStore/getAll
    * @param {string} objectStore
    * @param {any|IDBKeyRange} key
    * @param {number} count
    * @returns {object[]}Promise that resolves into all the entries from the specified object store.
    */
   getAll: async (objectStore, key, count) => {
      if (objectStore === undefined) {
         throw new Error("No object store supplied");
      }
      const args = argTreater([key, count]);
      return IDBWorker.postMessage({
         type: "getAll",
         objectStore: objectStore,
         args: args,
      });
   },
   /**
    * see https://developer.mozilla.org/en-US/docs/Web/API/IDBObjectStore/getKey
    * @param {string} objectStore
    * @param {any|IDBKeyRange} key
    * @returns {any} Promise that resolves into the object that matches the key (or first result, if provided a range of keys).
    */
   getKey: async (objectStore, key) => {
      if (objectStore === undefined) {
         throw new Error("No object store supplied");
      }
      if (key === undefined) {
         throw new Error("No key supplied");
      }
      const args = argTreater([key]);
      return IDBWorker.postMessage({
         type: "getKey",
         objectStore: objectStore,
         args: args,
      });
   },
   /**
    * getAllKeys takes an object store, as well as optional query and count arguments.
    * See https://developer.mozilla.org/en-US/docs/Web/API/IDBObjectStore/getAllKeys for details.
    * @param {string} objectStore
    * @param {IDBKeyRange} query
    * @param {number} count
    * @returns {any[]} Promise which resolves into all keys in a given object store or object store based on the query.
    * If count is provided, returns that many keys.
    */
   getAllKeys: async (objectStore, query, count) => {
      if (objectStore === undefined) {
         throw new Error("No object store supplied");
      }
      const args = argTreater([query, count]);
      return IDBWorker.postMessage({
         type: "getAllKeys",
         objectStore: objectStore,
         args: args,
      });
   },
   /**
    * Add places an object into your object store.  If an object already exists with the same key, this will fail.
    * see https://developer.mozilla.org/en-US/docs/Web/API/IDBObjectStore/add
    * @param {string} objectStore
    * @param {any} value
    * @param {any} key
    * @returns {any} Promise which resolves into the key of the successfully added object.
    */
   add: async (objectStore, value, key) => {
      if (objectStore === undefined) {
         throw new Error("No object store supplied");
      }
      if (value === undefined) {
         throw new Error("No object supplied");
      }
      const args = argTreater([value, key]);
      return IDBWorker.postMessage({
         type: "add",
         objectStore: objectStore,
         args: args,
      });
   },
   /**
    * AddAll places an array of objects into an object store. If an object already exists with the same key, this will fail.  For Debugging,
    * try iterating through your array with add for better error messages. see https://developer.mozilla.org/en-US/docs/Web/API/IDBObjectStore/add
    * @param {string} objectStore
    * @param {object[]} values
    * @returns {bool} Promise which resolve to true.  Throws error if unsuccessful.
    */
   addAll: async (objectStore, values) => {
      if (objectStore === undefined) {
         throw new Error("No object store supplied");
      }
      if (values === undefined) {
         throw new Error("No objects supplied");
      }
      return IDBWorker.postMessage({
         type: "addAll",
         objectStore: objectStore,
         objectArray: values,
      });
   },
   /**
    * Put places an object into IndexedDB. see https://developer.mozilla.org/en-US/docs/Web/API/IDBObjectStore/put
    * @param {string} objectStore
    * @param {object} value
    * @param {any} key
    * @returns {any} Promise which resolves into the key of the successfully added object.
    */
   put: async (objectStore, value, key) => {
      if (objectStore === undefined) {
         throw new Error("No object store supplied");
      }
      if (value === undefined) {
         throw new Error("No object supplied");
      }
      const args = argTreater([value, key]);
      return IDBWorker.postMessage({
         type: "put",
         objectStore: objectStore,
         args: args,
      });
   },
   /**
    * PutAll batches an array of objects, placing them into IndexedDB. Returns added objects if successful.  For debugging, try calling your iterating through your array
    * using put to get better error messages.  see https://developer.mozilla.org/en-US/docs/Web/API/IDBObjectStore/put
    * @param {string} objectStore
    * @param {object[]} values
    * @returns {object[]} Promise which resolves to the objects provided.  Throws error if unsuccessful.
    *
    */
   putAll: async (objectStore, values) => {
      if (objectStore === undefined) {
         throw new Error("No object store supplied");
      }
      if (values === undefined) {
         throw new Error("No objects supplied");
      }
      return IDBWorker.postMessage({
         type: "putAll",
         objectStore: objectStore,
         objectArray: values,
      });
   },
   /**
    * Delete removes an object from indexedDB based on the key provided.  see https://developer.mozilla.org/en-US/docs/Web/API/IDBObjectStore/delete
    * @param {string} objectStore
    * @param {any|IDBKeyRange} key
    * @returns {bool} True.  Throws error if unsuccessful
    */
   delete: async (objectStore, key) => {
      if (objectStore === undefined) {
         throw new Error("No object store supplied");
      }
      if (key === undefined) {
         throw new Error("No key supplied");
      }
      const args = argTreater([key]);
      return IDBWorker.postMessage({
         type: "delete",
         objectStore: objectStore,
         args: args,
      });
   },
   /**
    * Delete all removes several objects from indexedDB based on the keys provided.  see https://developer.mozilla.org/en-US/docs/Web/API/IDBObjectStore/delete
    * @param {string} objectStore
    * @param {any|IDBKeyRange} key
    * @returns {bool} True.  Throws error if unsuccessful
    */
   deleteAll: async (objectStore, keys) => {
      if (objectStore === undefined) {
         throw new Error("No object store supplied");
      }
      if (keys === undefined) {
         throw new Error("no keys supplied");
      }
      return IDBWorker.postMessage({
         type: "deleteAll",
         objectStore: objectStore,
         keyArray: keys,
      });
   },
   /**
    * counts entries based on key/keyrange, otherwise the counts the entire object store.
    * See https://developer.mozilla.org/en-US/docs/Web/API/IDBObjectStore/count
    * @param {string} objectStore
    * @param {any|IDBKeyRange} key
    * @returns {number} count of items within key range or if not provided, of items in across the objectStore.
    *
    */
   count: async (objectStore, query) => {
      if (objectStore === undefined) {
         throw new Error("No object store supplied");
      }
      const args = argTreater([query]);
      return IDBWorker.postMessage({
         type: "count",
         objectStore: objectStore,
         args: args,
      });
   },
   /**
    * Clear removes all entries from an object store. See https://developer.mozilla.org/en-US/docs/Web/API/IDBObjectStore/clear
    * @param {string} objectStore
    * @returns {bool} True.  Throws error if unsuccessful
    */
   clear: async (objectStore) => {
      if (objectStore === undefined) {
         throw new Error("No object store supplied");
      }
      return IDBWorker.postMessage({ type: "clear", objectStore: objectStore });
   },
};

export default function getIndexedDB() {
   return ECLIndexedDBWrapper;
}
